import {ready} from "./ready";

ready(() => {

	/**
	 * formTracking.js
	 *
	 * Handles form validation and AJAX submission for the order tracking form.
	 *
	 * Relies on `ajax_vars` being available as a global object injected via
	 * wp_localize_script, exposing:
	 *   - ajax_vars.ajax_url  →  WordPress AJAX endpoint
	 *   - ajax_vars.nonce     →  WordPress nonce for ajax_tracking_nonce
	 *
	 * WordPress functions.php must register both hooks:
	 *   add_action( 'wp_ajax_ajaxTracking', 'ajax_tracking' );
	 *   add_action( 'wp_ajax_nopriv_ajaxTracking', 'ajax_tracking' );
	 */


	const formTracking = document.querySelector('#formTracking');
	const submitBtn    = formTracking?.querySelector('[type="submit"]');
	const stepsEl      = document.querySelector('.steps');

	if (formTracking)
	{
		// Validation ======================================================

		/**
		 * Marks a field's parent wrapper as invalid and shows the correct
		 * error message.
		 *
		 * Expected DOM structure:
		 *   <div class="form-field">
		 *     <input name="fieldName" />
		 *     <span class="form-error form-error-empty">Required</span>
		 *     <span class="form-error form-error-format">Invalid format</span>
		 *   </div>
		 *
		 * @param {Element}           input  - The input that failed validation.
		 * @param {'empty'|'format'}  type   - Which error variant to display.
		 */
		const showFieldError = (input, type) => {
			const wrapper = input.closest('.form-field');

			if (!wrapper)
			{
				return;
			}

			wrapper.classList.add('has-error');

			// Reset any previous error state on this field
			wrapper.querySelectorAll('.form-error').forEach(el => {
				el.style.display = 'none';
			});

			const target = wrapper.querySelector('.form-error-' + type);

			// Show the correct error message
			if (target)
			{
				target.style.display = '';
			}
		};

		/**
		 * Clears the error state for an input's wrapper.
		 *
		 * @param {Element} input
		 */
		const clearFieldError = (input) => {
			const wrapper = input.closest('.form-field');

			if (!wrapper)
			{
				return;
			}

			wrapper.classList.remove('has-error');

			wrapper.querySelectorAll('.form-error').forEach(el => {
				el.style.display = 'none';
			});
		};

		/**
		 * Validates all tracking-form fields.
		 * Populates inline error messages and returns true when the form is valid.
		 *
		 * @returns {boolean}
		 */
		const validateForm = () => {
			let isValid = true;

			const clientNumberInput = formTracking.querySelector('[name="clientNumber"]');
			const orderNumberInput  = formTracking.querySelector('[name="orderNumber"]');
			const orderTypeInput    = formTracking.querySelector('[name="orderType"]');

			const clientNumber = clientNumberInput?.value.trim() ?? '';
			const orderNumber  = orderNumberInput?.value.trim() ?? '';
			const orderType    = orderTypeInput?.value.trim().toUpperCase() ?? '';	// Normalize to uppercase so the backend receives a consistent value and the regex check below stays simple.

			// Client number
			clearFieldError(clientNumberInput);

			if (!clientNumber)
			{
				showFieldError(clientNumberInput, 'empty');
				isValid = false;
			}
			else if (!/^\d{10}$/.test(clientNumber))
			{
				showFieldError(clientNumberInput, 'format');
				isValid = false;
			}

			// Order number
			clearFieldError(orderNumberInput);

			if (!orderNumber)
			{
				showFieldError(orderNumberInput, 'empty');
				isValid = false;
			}
			else if (!/^\d{1,7}$/.test(orderNumber))
			{
				showFieldError(orderNumberInput, 'format');
				isValid = false;
			}

			// Order type (single letter; validated after toUpperCase() normalization)
			clearFieldError(orderTypeInput);

			if (!orderType)
			{
				showFieldError(orderTypeInput, 'empty');
				isValid = false;
			}
			else if (!/^[A-Z]$/.test(orderType))
			{
				showFieldError(orderTypeInput, 'format');
				isValid = false;
			}

			return isValid;
		};


		// AJAX submission ======================================================

		/**
		 * Posts the form data to the WordPress AJAX endpoint and renders the HTML response inside `.steps`
		 *
		 * @param {Object} payload - Key/value pairs sent to PHP.
		 */
		const submitTrackingForm = async (payload) => {
			const submitWrapper = submitBtn.closest('.submit-field');

			// Show a loading indicator on the submit button's parent wrapper.
			submitWrapper?.classList.add('loading');

			// Hide any previously rendered results before the new request completes.
			if (stepsEl)
			{
				await new Promise(resolve => {
					stepsEl.style.display = 'block';
					stepsEl.classList.add('is-fading');

					setTimeout(() => {
						stepsEl.style.display = 'none';
						stepsEl.classList.remove('is-fading');
						resolve();
					}, 300); // match the CSS transition duration
				});
			}

			// URLSearchParams produces application/x-www-form-urlencoded encoding,
			// which is what the WordPress AJAX handler expects.
			const body = new URLSearchParams({
				action: 'ajaxTracking', // must match the suffix used in wp_ajax_{action} hooks.
				nonce:  ajax_vars.nonce,
				...payload,
			});

			try
			{
				const response = await fetch(ajax_vars.ajax_url, {
					method:  'POST',
					headers: {'Content-Type': 'application/x-www-form-urlencoded'},
					body:    body.toString(),
				});

				if (!response.ok)
				{
					throw new Error('Server error: ' + response.status);
				}

				const html = await response.text();

				if (stepsEl)
				{
					stepsEl.innerHTML = html;
				}
			} catch (err)
			{
				console.error('[formTracking] AJAX request failed:', err);

				if (stepsEl)
				{
					stepsEl.innerHTML = '<div class="step not-found"><span>' + err + '</span></div>';
				}
			} finally
			{
				// Fade in (after setting innerHTML)
				stepsEl.classList.add('is-fading');
				stepsEl.style.display = 'block';
				requestAnimationFrame(() => stepsEl.classList.remove('is-fading'));

				const top = stepsEl.getBoundingClientRect().top + window.scrollY - 100;
				window.scrollTo({top, behavior: 'smooth'});

				// Always remove the loading state, even if the request failed.
				submitWrapper?.classList.remove('loading');
			}
		};


		// Event binding ======================================================

		submitBtn.addEventListener('click', async (e) => {
			e.preventDefault();

			if (!validateForm())
			{
				return;
			}

			await submitTrackingForm({
				orderNumber:  formTracking.querySelector('[name="orderNumber"]').value.trim(),
				orderType:    formTracking.querySelector('[name="orderType"]').value.trim().toUpperCase(),
				clientNumber: formTracking.querySelector('[name="clientNumber"]').value.trim(),
			});
		});
	}

}); // END ready